import type { TFunction } from 'i18next';
import type { Dependent } from '../employeeTypes';
import type { TableColumn } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { renderCell } from '../../../components/ui/tableTemplate/cellRenderers';
import { resolveLabeledCode } from '../../../lib/translateCode';
import { DependentBoolCell } from '../components/detail/DependentBoolCell';

/**
 * Returns column definitions for the dependents table, compatible with TableTemplate<Dependent>.
 *
 * Accepts `t` as a parameter so that boolean render functions can close over the translator —
 * TableColumn.render only receives the row, so `t` cannot be passed at call time.
 *
 * Empty-state strategy differs from FieldGrid: table cells render the '-'
 * placeholder as a plain string directly in the render function.
 * We don't propagate null here because TableTemplate has no shared FieldItem
 * component — each cell renders its own ReactNode, so the '-' is the simplest
 * and most readable choice at this level.
 */
export function getDependentColumns(t: TFunction): TableColumn<Dependent>[] {
  return [
    {
      key: 'last_name',
      labelKey: 'employeeDetail.fields.dependentLastName',
      width: 140,
      render: d => renderCell('text', d.last_name ?? d.name ?? '-', { t }),
    },
    {
      key: 'first_name',
      labelKey: 'employeeDetail.fields.dependentFirstName',
      width: 140,
      render: d => renderCell('text', d.first_name ?? '-', { t }),
    },
    {
      key: 'relationship',
      labelKey: 'employeeDetail.fields.dependentRelationship',
      width: 130,
      render: d => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.relationship', d.relationship) || '-', { t }),
    },
    {
      key: 'birthdate',
      labelKey: 'employeeDetail.fields.dependentBirthdate',
      width: 120,
      render: d => renderCell('date', d.birthdate ?? '-', { t }),
    },
    {
      key: 'birth_place',
      labelKey: 'employeeDetail.fields.dependentBirthPlace',
      width: 130,
      render: d => renderCell('text', d.birth_place ?? '-', { t }),
    },
    {
      key: 'gender',
      labelKey: 'employeeDetail.fields.dependentGender',
      width: 100,
      render: d => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.gender', d.gender) || '-', { t }),
    },
    {
      key: 'nationality',
      labelKey: 'employeeDetail.fields.dependentNationality',
      width: 120,
      render: d => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.country', d.nationality) || '-', { t }),
    },
    {
      key: 'parent_name',
      labelKey: 'employeeDetail.fields.dependentParentName',
      width: 140,
      render: d => renderCell('text', d.parent_name ?? '-', { t }),
    },
    {
      key: 'dependent_status',
      labelKey: 'employeeDetail.fields.dependentStatus',
      width: 120,
      render: d => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.dependentStatus', d.dependent_status) || '-', { t }),
    },
    {
      key: 'marital_status',
      labelKey: 'employeeDetail.fields.dependentMaritalStatus',
      width: 130,
      render: d => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.maritalStatus', d.marital_status) || '-', { t }),
    },
    {
      key: 'disability',
      labelKey: 'employeeDetail.fields.dependentDisability',
      width: 100,
      render: d => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.yesNo', d.disability) || '-', { t }),
    },
    {
      key: 'custody',
      labelKey: 'employeeDetail.fields.dependentCustody',
      width: 100,
      render: d => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.yesNo', d.custody) || '-', { t }),
    },
    {
      key: 'working',
      labelKey: 'employeeDetail.fields.dependentWorking',
      width: 100,
      render: d => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.yesNo', d.working) || '-', { t }),
    },
    {
      key: 'location',
      labelKey: 'employeeDetail.fields.dependentLocation',
      width: 130,
      render: d => renderCell('text', d.location ?? '-', { t }),
    },
    {
      key: 'document_type',
      labelKey: 'employeeDetail.fields.dependentDocumentType',
      width: 130,
      render: d => renderCell('text', d.document_type ?? '-', { t }),
    },
    {
      key: 'document_validity_date',
      labelKey: 'employeeDetail.fields.dependentDocumentValidityDate',
      width: 160,
      render: d => renderCell('date', d.document_validity_date ?? '-', { t }),
    },
    {
      key: 'document_file',
      labelKey: 'employeeDetail.fields.dependentDocumentFile',
      width: 130,
      render: d => renderCell('text', d.document_file ?? '-', { t }),
    },
    {
      key: 'document_checked',
      labelKey: 'employeeDetail.fields.dependentDocumentChecked',
      width: 130,
      render: d => <DependentBoolCell value={d.document_checked} t={t} />,
    },
    {
      key: 'is_active',
      labelKey: 'employeeDetail.fields.dependentActive',
      width: 100,
      render: d => <DependentBoolCell value={d.is_active} t={t} />,
    },
  ];
}
